import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ChangePasswordModal from "./components/ChangePasswordModal";
import axios from "axios";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import PatientDetail from "./pages/PatientDetail";
import Encounters from "./pages/Encounters";
import EncounterDetail from "./pages/EncounterDetail";
import Billing from "./pages/Billing";
import InvoiceDetail from "./pages/InvoiceDetail";
import StaffManagement from "./pages/StaffManagement";
import Tasks from "./pages/Tasks";
import Productivity from "./pages/Productivity";
import Appointments from "./pages/Appointments";
import ServiceCatalogue from "./pages/ServiceCatalogue";
import InsuranceProviders from "./pages/InsuranceProviders";
import OutstandingBalances from "./pages/OutstandingBalances";
import Library from "./pages/Library";
import PatientQueue from "./pages/PatientQueue";
import Inventory from "./pages/Inventory";
import LowStock from "./pages/LowStock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProtectedRoute = ({ children }) => {
  const { user, loading, forcePasswordChange, setForcePasswordChange } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Handle forced password change
  if (forcePasswordChange) {
    const handlePasswordChange = async (newPassword) => {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword });
      setForcePasswordChange(false);
    };
    
    return (
      <>
        {children}
        <ChangePasswordModal
          isOpen={true}
          onPasswordChanged={handlePasswordChange}
        />
      </>
    );
  }
  
  return children;
};

// Route that requires clinical access (owner or doctor)
const ClinicalRoute = ({ children }) => {
  const { user, loading, canAccessClinical, forcePasswordChange, setForcePasswordChange } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!canAccessClinical) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Handle forced password change
  if (forcePasswordChange) {
    const handlePasswordChange = async (newPassword) => {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword });
      setForcePasswordChange(false);
    };
    
    return (
      <>
        {children}
        <ChangePasswordModal
          isOpen={true}
          onPasswordChanged={handlePasswordChange}
        />
      </>
    );
  }
  
  return children;
};

// Route that requires billing access (owner only)
const BillingRoute = ({ children }) => {
  const { user, loading, canAccessBilling, forcePasswordChange, setForcePasswordChange } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!canAccessBilling) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Handle forced password change
  if (forcePasswordChange) {
    const handlePasswordChange = async (newPassword) => {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword });
      setForcePasswordChange(false);
    };
    
    return (
      <>
        {children}
        <ChangePasswordModal
          isOpen={true}
          onPasswordChanged={handlePasswordChange}
        />
      </>
    );
  }
  
  return children;
};

// Route that requires admin access (owner only)
const AdminRoute = ({ children }) => {
  const { user, loading, isOwner, forcePasswordChange, setForcePasswordChange } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isOwner) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Handle forced password change
  if (forcePasswordChange) {
    const handlePasswordChange = async (newPassword) => {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword });
      setForcePasswordChange(false);
    };
    
    return (
      <>
        {children}
        <ChangePasswordModal
          isOpen={true}
          onPasswordChanged={handlePasswordChange}
        />
      </>
    );
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/patients" element={
        <ProtectedRoute>
          <Patients />
        </ProtectedRoute>
      } />
      <Route path="/patients/:id" element={
        <ProtectedRoute>
          <PatientDetail />
        </ProtectedRoute>
      } />
      {/* Patient Queue - Reception Flow */}
      <Route path="/queue" element={
        <ProtectedRoute>
          <PatientQueue />
        </ProtectedRoute>
      } />
      {/* Clinical routes - Owner and Doctor only */}
      <Route path="/encounters" element={
        <ClinicalRoute>
          <Encounters />
        </ClinicalRoute>
      } />
      <Route path="/encounters/:id" element={
        <ClinicalRoute>
          <EncounterDetail />
        </ClinicalRoute>
      } />
      {/* Billing routes - Owner only */}
      <Route path="/billing" element={
        <BillingRoute>
          <Billing />
        </BillingRoute>
      } />
      <Route path="/billing/:id" element={
        <BillingRoute>
          <InvoiceDetail />
        </BillingRoute>
      } />
      {/* Admin routes - Owner only */}
      <Route path="/staff" element={
        <AdminRoute>
          <StaffManagement />
        </AdminRoute>
      } />
      <Route path="/productivity" element={
        <AdminRoute>
          <Productivity />
        </AdminRoute>
      } />
      {/* Service Catalogue - Admin only */}
      <Route path="/services" element={
        <AdminRoute>
          <ServiceCatalogue />
        </AdminRoute>
      } />
      {/* Insurance Providers - Admin only (Phase 6.4) */}
      <Route path="/insurance-providers" element={
        <AdminRoute>
          <InsuranceProviders />
        </AdminRoute>
      } />
      {/* Outstanding Balances - Cashier/Owner/Reception (Phase 6.5) */}
      <Route path="/outstanding-balances" element={
        <BillingRoute>
          <OutstandingBalances />
        </BillingRoute>
      } />
      {/* Library - All authenticated users (Phase 6.5) */}
      <Route path="/library" element={
        <ProtectedRoute>
          <Library />
        </ProtectedRoute>
      } />
      {/* Inventory - Owner Admin only (Packet A) */}
      <Route path="/inventory" element={
        <AdminRoute>
          <Inventory />
        </AdminRoute>
      } />
      <Route path="/inventory/low-stock" element={
        <AdminRoute>
          <LowStock />
        </AdminRoute>
      } />
      {/* Tasks - All authenticated users */}
      <Route path="/tasks" element={
        <ProtectedRoute>
          <Tasks />
        </ProtectedRoute>
      } />
      {/* Appointments - All authenticated users */}
      <Route path="/appointments" element={
        <ProtectedRoute>
          <Appointments />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
